import { getFormTabErrorCounts } from '../../../../../../lib/formTabErrors';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Ban,
  Boxes,
  Cable,
  CircleDollarSign,
  Cog,
  ListTree,
  Package,
  SlidersHorizontal,
} from 'lucide-react';
import { useForm, type FieldErrors } from 'react-hook-form';
import {
  DashboardPageFormActions,
  DashboardPageFormTabPanel,
  DashboardPageFormTabList,
  DashboardPageFormTab,
} from '@shigo-ui/dashboard-ui';
import { Button, Form } from '@shigo-ui/ui';

import { EditorSectionTabs } from '../../../../../../components/forms/EditorSectionTabs';
import { useEditorSectionControls } from '../../../../../../components/forms/useEditorSection';
import { dashboardT } from '../../../../../../lib/messages';
import { PlacementEditorTab, PlacementType, type PlacementPublisher } from '../../../model';
import { createPlacementFormSchema, type PlacementFormValue } from '../../../placementFormSchema';
import { PlacementAdUnitFields } from '../PlacementAdUnitFields';
import { PlacementDeliveryFields } from '../PlacementDeliveryFields';
import { PlacementExclusionsFields } from '../PlacementExclusionsFields';
import { PlacementGeneralFields } from '../PlacementGeneralFields';
import { PlacementIntegrationsFields } from '../PlacementIntegrationsFields';
import { PlacementPricingFields } from '../PlacementPricingFields';
import { PlacementTargetingFields } from '../PlacementTargetingFields';
import { PlacementXpMappingsFields } from '../PlacementXpMappingsFields';

export function PlacementEditorForm(props: {
  initialValue: PlacementFormValue;
  initialPublisher?: PlacementPublisher | undefined;
  placementId?: string | undefined;
  secondaryId?: string | undefined;
  saving: boolean;
  bidCacheAllowed: boolean;
  stitcherAllowed: boolean;
  a2vAllowed: boolean;
  adUnitAllowed: boolean;
  onCancel: () => void;
  onSubmit: (value: PlacementFormValue) => Promise<void>;
}) {
  'use no memo';
  const form = useForm<PlacementFormValue>({
    defaultValues: props.initialValue,
    resolver: zodResolver(createPlacementFormSchema({ validateAdUnit: props.adUnitAllowed })),
    mode: 'onSubmit',
  });
  const isAdUnit = props.initialValue.type === PlacementType.AdUnit;
  // Dual mode is always available, so every non ad unit placement has delivery settings.
  const showDelivery = !isAdUnit;
  const availableTabs = Object.values(PlacementEditorTab).filter(
    (tab) =>
      (tab !== PlacementEditorTab.Delivery || showDelivery) &&
      (tab !== PlacementEditorTab.AdUnit || (isAdUnit && props.adUnitAllowed)),
  );
  const [editorSection, setActiveTab] = useEditorSectionControls(
    PlacementEditorTab.General,
    availableTabs,
  );
  const deliveryTabLabel = props.stitcherAllowed
    ? ('placements.tabs.cacheStitcher' as const)
    : ('placements.tabs.bidCache' as const);

  function invalidSubmit(errors: FieldErrors<PlacementFormValue>) {
    const counts = getPlacementTabErrors(errors);
    const firstInvalidSection = availableTabs.find((tab) => counts[tab]);
    if (firstInvalidSection) setActiveTab(firstInvalidSection);
  }

  const tabErrors = getPlacementTabErrors(form.formState.errors);

  const visibleTabs = new Set(availableTabs);
  const sections = (
    [
      [PlacementEditorTab.General, Cog, 'placements.tabs.general'],
      [PlacementEditorTab.Pricing, CircleDollarSign, 'placements.tabs.pricing'],
      [PlacementEditorTab.Integrations, Cable, 'placements.tabs.integrations'],
      [PlacementEditorTab.Targeting, SlidersHorizontal, 'placements.tabs.targeting'],
      [PlacementEditorTab.Exclusions, Ban, 'placements.tabs.exclusions'],
      [PlacementEditorTab.Delivery, Package, deliveryTabLabel],
      [PlacementEditorTab.AdUnit, Boxes, 'placements.tabs.adUnit'],
      [PlacementEditorTab.XpMappings, ListTree, 'placements.tabs.xp'],
    ] as const
  )
    .filter(([value]) => visibleTabs.has(value))
    .map(([value, Icon, label]) => ({
      value,
      label: dashboardT(label),
      description: dashboardT(`${label}.description`),
      icon: <Icon aria-hidden="true" />,
      errorCount: tabErrors[value],
    }));

  return (
    <Form {...form}>
      <form
        noValidate
        onSubmit={(event) =>
          void form.handleSubmit((value) => props.onSubmit(value), invalidSubmit)(event)
        }
      >
        <EditorSectionTabs controls={editorSection}>
          <DashboardPageFormTabList
            navigationLabel={dashboardT('forms.configuration')}
            reviewLabel={dashboardT('forms.reviewErrors')}
            errorLabel={(count) => dashboardT('common.validationErrors', { count })}
          >
            {sections.map((section) => (
              <DashboardPageFormTab
                key={section.value}
                value={section.value}
                description={section.description}
                icon={section.icon}
                errorCount={section.errorCount}
                errorLabel={(count) => dashboardT('common.validationErrors', { count })}
              >
                {section.label}
              </DashboardPageFormTab>
            ))}
          </DashboardPageFormTabList>
          <DashboardPageFormTabPanel value={PlacementEditorTab.General}>
            <PlacementGeneralFields
              editing={Boolean(props.placementId)}
              initialPublisher={props.initialPublisher}
            />
          </DashboardPageFormTabPanel>
          <DashboardPageFormTabPanel value={PlacementEditorTab.Pricing}>
            <PlacementPricingFields publisher={props.initialPublisher} />
          </DashboardPageFormTabPanel>
          <DashboardPageFormTabPanel value={PlacementEditorTab.Integrations}>
            <PlacementIntegrationsFields />
          </DashboardPageFormTabPanel>
          <DashboardPageFormTabPanel value={PlacementEditorTab.Targeting}>
            <PlacementTargetingFields />
          </DashboardPageFormTabPanel>
          <DashboardPageFormTabPanel value={PlacementEditorTab.Exclusions}>
            <PlacementExclusionsFields />
          </DashboardPageFormTabPanel>
          {showDelivery ? (
            <DashboardPageFormTabPanel value={PlacementEditorTab.Delivery}>
              <PlacementDeliveryFields
                bidCacheAllowed={props.bidCacheAllowed}
                stitcherAllowed={props.stitcherAllowed}
                a2vAllowed={props.a2vAllowed}
              />
            </DashboardPageFormTabPanel>
          ) : null}
          {isAdUnit && props.adUnitAllowed ? (
            <DashboardPageFormTabPanel value={PlacementEditorTab.AdUnit}>
              <PlacementAdUnitFields
                placementId={props.placementId}
                secondaryId={props.secondaryId}
              />
            </DashboardPageFormTabPanel>
          ) : null}
          <DashboardPageFormTabPanel value={PlacementEditorTab.XpMappings}>
            <PlacementXpMappingsFields />
          </DashboardPageFormTabPanel>
        </EditorSectionTabs>
        <DashboardPageFormActions saveShortcutLabel={dashboardT('forms.saveShortcut')}>
          <Button type="button" variant="outline" disabled={props.saving} onClick={props.onCancel}>
            {dashboardT('placements.cancel')}
          </Button>
          <Button type="submit" disabled={props.saving}>
            {dashboardT(props.saving ? 'placements.saving' : 'placements.save')}
          </Button>
        </DashboardPageFormActions>
      </form>
    </Form>
  );
}

function getPlacementTabErrors(errors: FieldErrors<PlacementFormValue>) {
  return getFormTabErrorCounts(
    errors,
    {
      [PlacementEditorTab.Pricing]: ['pricingType', 'pricing', 'lineItemMargin'],
      [PlacementEditorTab.Integrations]: ['demandIntegrations'],
      [PlacementEditorTab.Targeting]: ['targetings'],
      [PlacementEditorTab.Exclusions]: [
        'exclusionsEnabled',
        'advertiserDomainsText',
        'excludedCreativeIdsText',
        'excludedIabCategoryIds',
      ],
      [PlacementEditorTab.Delivery]: [
        'bcAvEnabled',
        'bcVideoEnabled',
        'stitcherEnabled',
        'stAcceptPercentageOfFloor',
        'stMaxBids',
        'stMaxAvBids',
        'stMaxVideoBids',
        'stMaxBidsFromCache',
        'stPoddingFloor',
        'stFeeCpm',
        'dmShortTimeoutMs',
        'dmLongTimeoutMs',
        'stDedupeEnabled',
        'dmEnabled',
      ],
      [PlacementEditorTab.AdUnit]: ['adUnit'],
      [PlacementEditorTab.XpMappings]: [
        'xpParametersMappings',
        'overridePublisherXpParametersMapping',
      ],
    },
    PlacementEditorTab.General,
  );
}
